import AsyncStorage from "@react-native-async-storage/async-storage";
import axios from "axios";
import moment from "moment";
import {
  BASE_URL,
  DATETIME_FORMAT_24H,
  DATETIME_FORMAT_ISO,
  TIME_FORMAT_24H_MINUTES,
  TIME_FORMAT_UTC,
} from "shared/constants";

const HTTP_STATUS_FORBIDDEN = 403;
const HTTP_STATUS_NOT_AUTHORIZED = 401;

export const DATETIME_REGEX = /"(\d{4}-\d{2}-\d{2}.\s*\d?\d:\d\d:?\d?\d?)"/g;
export const TIME_REGEX = /"(\d?\d:\d\d:?\d?\d?)"/g;

const DATETIME_VALUE_RE = /^\d{4}-\d{2}-\d{2}.\s*\d?\d:\d\d:?\d?\d?$/;
const TIME_VALUE_RE = /^\d?\d:\d\d:?\d?\d?$/;

const getLocalTimezoneOffset = (date) => {
  return moment(date, true).isValid()
    ? moment(date).format("Z")
    : moment(date, DATETIME_FORMAT_ISO).format("Z");
};

// Convert time strings to the given timezone
const convertTime = (value, convertToLocal) => {
  const timezoneOffset = getLocalTimezoneOffset();

  // Turn time input into a valid moment object and converted it to UTC time format
  const convertedToValidTimeFormat = moment(
    value,
    TIME_FORMAT_24H_MINUTES,
  ).format(TIME_FORMAT_UTC);

  // Split the converted datetime format to get the time and offset
  const extractOperation = timezoneOffset.match(/[+-]/);
  const timeOffsetSplit = convertedToValidTimeFormat.split(extractOperation[0]);
  const offsetSplit = timeOffsetSplit[1].split(":");
  const timeSplit = timeOffsetSplit[0].split(":");
  const operation = convertToLocal
    ? extractOperation[0] === "+"
      ? "-"
      : "+"
    : extractOperation[0];
  const hour = Number.parseInt(timeSplit[0], 10);
  const minute = Number.parseInt(timeSplit[1], 10);
  const offsetValueHour = offsetSplit[0];
  const offsetValueMinute = offsetSplit[1];

  // Add/subtract the offset to the original hour
  let newHour = 0;
  let newMinute = 0;
  if (operation[0] === "+") {
    newMinute = minute - Number.parseInt(offsetValueMinute, 10);
    if (newMinute < 0) {
      newMinute = 60 + newMinute;
      newHour = hour - 1;
    }
    newHour = newHour + hour - Number.parseInt(offsetValueHour, 10);
    if (newHour < 0) {
      newHour = 24 + newHour;
    }
  } else {
    newMinute = minute + Number.parseInt(offsetValueMinute, 10);
    if (newMinute > 59) {
      newMinute = newMinute - 60;
      newHour = hour + 1;
    }
    newHour = newHour + hour + Number.parseInt(offsetValueHour, 10);
    if (newHour > 24) {
      newHour = newHour % 24;
    }
  }

  // Build the new datetime/time string
  newMinute = newMinute < 10 ? "0" + newMinute : newMinute;
  newHour = newHour < 10 ? "0" + newHour : newHour;
  const newTime = newHour + ":" + newMinute + ":" + timeSplit[2];
  return newTime;
};

// Add timezone offsets to all response datetime and time strings
const transformValue = (str, convertToLocal) => {
  if (DATETIME_VALUE_RE.test(str)) {
    const timezoneOffset = convertToLocal
      ? "+00:00"
      : getLocalTimezoneOffset(str);
    return `${moment(str, DATETIME_FORMAT_24H).format(DATETIME_FORMAT_24H)}${timezoneOffset}`;
  }
  if (convertToLocal && TIME_VALUE_RE.test(str)) {
    return convertTime(str, convertToLocal);
  }
  return str;
};
const transformData = (data, convertToLocal) => {
  if (typeof data === "string") {
    return transformValue(data, convertToLocal);
  }
  if (Array.isArray(data)) {
    return data.map((item) => transformData(item, convertToLocal));
  }
  if (data !== null && typeof data === "object") {
    const result = {};
    for (const key of Object.keys(data)) {
      result[key] = transformData(data[key], convertToLocal);
    }
    return result;
  }
  return data;
};

const AxiosService = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
});

AxiosService.interceptors.request.use((request) => {
  if (!request.data) {
    return request;
  }

  request.data = transformData(request.data, false);

  return request;
});

AxiosService.interceptors.response.use(
  (response) => {
    response.data = transformData(response.data, true);

    return response;
  },
  (error) => {
    const status = error?.response?.status;

    if (status === 350) {
      const transformedData = transformData(error.response.data, true);
      return Promise.resolve({
        ...error.response,
        data: transformedData,
        status: error.response.status,
        statusText: error.response.statusText,
      });
    }

    if (
      status === HTTP_STATUS_NOT_AUTHORIZED ||
      status === HTTP_STATUS_FORBIDDEN
    ) {
      AsyncStorage.removeItem("userToken");
    }
    return Promise.reject(error);
  },
);

export default AxiosService;
